import type { Rect } from "@/lib/math/rect";

export const FRAME_LABEL_HEIGHT_PX = 20;
export const FRAME_LABEL_OFFSET_Y_PX = 22;
export const FRAME_LABEL_MIN_WIDTH_PX = 64;
export const FRAME_LABEL_MAX_WIDTH_PX = 224;
export const FRAME_LABEL_PADDING_X_PX = 4;
/** Reserved strip for edit/run script icons on attached-script frames. */
export const FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX = 34;

/** Hide top-level frame/component titles at 4% zoom and below. */
export const FRAME_LABEL_MIN_ZOOM = 0.04;

export function shouldShowFrameLabels(zoom: number): boolean {
  return zoom > FRAME_LABEL_MIN_ZOOM;
}

export interface FrameLabelGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  corners: Array<{ x: number; y: number }>;
}

export function frameLabelGeometryForCorners(
  frameCorners: Array<{ x: number; y: number }>,
  zoom: number,
  options?: { extraWidthPx?: number },
): FrameLabelGeometry | undefined {
  if (frameCorners.length < 4 || zoom <= 0) {
    return undefined;
  }

  const center = frameCorners.reduce(
    (acc, point) => ({ x: acc.x + point.x / frameCorners.length, y: acc.y + point.y / frameCorners.length }),
    { x: 0, y: 0 },
  );
  const edges = frameCorners.map((from, index) => {
    const to = frameCorners[(index + 1) % frameCorners.length]!;
    return {
      from,
      to,
      midpoint: { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
    };
  });
  const edge = edges.reduce((top, candidate) => candidate.midpoint.y < top.midpoint.y ? candidate : top, edges[0]!);

  let from = edge.from;
  let to = edge.to;
  let dx = to.x - from.x;
  let dy = to.y - from.y;
  if (dx < -0.0001 || (Math.abs(dx) <= 0.0001 && dy < 0)) {
    from = edge.to;
    to = edge.from;
    dx = to.x - from.x;
    dy = to.y - from.y;
  }

  const edgeLength = Math.max(0.0001, Math.hypot(dx, dy));
  const axisX = dx / edgeLength;
  const axisY = dy / edgeLength;
  const outwardRaw = { x: edge.midpoint.x - center.x, y: edge.midpoint.y - center.y };
  const outwardLength = Math.max(0.0001, Math.hypot(outwardRaw.x, outwardRaw.y));
  const outward = { x: outwardRaw.x / outwardLength, y: outwardRaw.y / outwardLength };
  const extraWidthPx = Math.max(0, options?.extraWidthPx ?? 0);
  const width = (Math.min(FRAME_LABEL_MAX_WIDTH_PX, Math.max(FRAME_LABEL_MIN_WIDTH_PX, edgeLength * zoom)) + extraWidthPx) / zoom;
  const height = FRAME_LABEL_HEIGHT_PX / zoom;
  const offset = FRAME_LABEL_OFFSET_Y_PX / zoom;
  const origin = {
    x: from.x + outward.x * offset,
    y: from.y + outward.y * offset,
  };
  const yAxis = { x: -outward.x, y: -outward.y };
  const topRight = { x: origin.x + axisX * width, y: origin.y + axisY * width };
  const bottomRight = { x: topRight.x + yAxis.x * height, y: topRight.y + yAxis.y * height };
  const bottomLeft = { x: origin.x + yAxis.x * height, y: origin.y + yAxis.y * height };

  return {
    x: origin.x,
    y: origin.y,
    width,
    height,
    rotation: Math.atan2(axisY, axisX) * 180 / Math.PI,
    corners: [origin, topRight, bottomRight, bottomLeft],
  };
}

export function boundsFromPoints(points: Array<{ x: number; y: number }>): Rect {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}
